/**
 * Rider Session contract — CORE-05B, REND-005, RIDER-057/058/059.
 *
 * The row schemas here are hand-written Zod, following `schedule.ts`, and are
 * NOT a substitute for the generated DB types: they describe the shape the
 * client and the Edge Function agree on, which is a subset of the table
 * (`count_lock_id`, `template_version`, generation audit columns are server
 * state a Rider never receives).
 *
 * Visibility is deliberately absent from this contract. Which Sessions a Rider
 * may see is a database policy (0060), not a request field — a client that
 * could ask for "the Diamond ones" is a client that could be tampered into
 * asking for them.
 */

import { z } from "zod";

const uuidSchema = z.string().uuid();
const offsetDateTimeSchema = z.string().datetime({ offset: true });

export const riderSessionDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected an ISO date (YYYY-MM-DD)")
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, "Expected a valid calendar date");

/** §3.2 fixed Session periods. */
export const riderSessionCodeSchema = z.enum(["A", "B", "C"]);

/** §6.2 — the two publication states. `SECURED` is not an access state. */
export const riderSessionAccessSchema = z.enum(["diamond", "all"]);

/** RIDER-031: rank is set manually by an authorized Admin; no auto-promotion. */
export const riderRankSchema = z.enum(["bronze", "silver", "gold", "diamond"]);

/**
 * REND-005: the Rider selects a maximum extension of 1, 2, or 3 hours BEFORE
 * the final secure action, and it is stored with the Secured Session. A literal
 * union, not `min(1).max(3)` — 2.5 hours is not a thing the operation has.
 */
export const riderSessionMaxExtensionSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);

/**
 * CORE-04 lifecycle states. `preStartWaiting` is a first-class state and not
 * "active but early" — REND-010 forbids showing it with an Active badge or
 * Auto-Assign-on copy, so the UI must be able to tell them apart.
 */
export const riderSessionStateSchema = z.enum([
  "secured",
  "pre_start_waiting",
  "active",
  "paused",
  "emergency_paused",
  "extended",
  "session_done",
  "unable_to_continue",
]);

/** CORE-04's five attendance results. `completed` IS the on-time result. */
export const riderSessionOutcomeSchema = z.enum([
  "completed",
  "late_start",
  "early_end",
  "incomplete",
  "no_show",
]);

/**
 * Report an Issue categories. Medical is 60 minutes of Emergency Pause, the
 * rest are 30 — the duration rule is stated, the category list is not approved
 * as final, which is why the column behind this is `text` and this enum is the
 * narrower of the two.
 */
export const riderSessionEmergencyCategorySchema = z.enum([
  "emergency",
  "vehicle",
  "dispatch",
  "technical",
  "medical",
]);

/** One Rider-visible Session record. One record is one card — RIDER-059. */
export const riderSessionSchema = z.object({
  id: uuidSchema,
  hubId: uuidSchema,
  serviceDate: riderSessionDateSchema,
  code: riderSessionCodeSchema,
  startsAt: offsetDateTimeSchema,
  endsAt: offsetDateTimeSchema,
  access: riderSessionAccessSchema,
  securedRiderId: uuidSchema.nullable(),
  securedAt: offsetDateTimeSchema.nullable(),
  maxExtensionHours: riderSessionMaxExtensionSchema.nullable(),
  state: riderSessionStateSchema,
  /** When the Rider tapped Start — NOT when the Session became Active. */
  startedAt: offsetDateTimeSchema.nullable(),
  /** When Auto-Assign came on. Null through the whole PreStartWaiting period. */
  activatedAt: offsetDateTimeSchema.nullable(),
  endedAt: offsetDateTimeSchema.nullable(),
  outcome: riderSessionOutcomeSchema.nullable(),
  /**
   * Live remaining-allowance input for the pause UI (90 − this). RIDER-055 bars
   * it from HISTORY, which is a different surface with a different query — see
   * `v_rider_session_history`. Present here because the running Session cannot
   * show a Rider how much pause they have left without it.
   */
  pauseMinutesUsed: z.number().int().min(0).max(90),
  /** RS-006: actual overrun, never the selected maximum. */
  actualExtensionMinutes: z.number().int().min(0),
});

/** §4.8 — the atomic secure action. */
export const secureRiderSessionSchema = z
  .object({
    sessionId: uuidSchema,
    maxExtensionHours: riderSessionMaxExtensionSchema,
  })
  .strict();

/**
 * §4.1–§4.6 — one generation batch for one service date and Dispatch Area.
 *
 * ⚠ TWO FIELDS, AND IT USED TO BE SIX. `cycleId`, `countLockId`,
 * `templateVersion` and `finalTherapistCount` were request fields only because
 * CORE-05A's Friday 7:00 PM count lock did not exist yet (OQ-66, OQ-70), and
 * both entries said the same thing about the day it landed: the endpoint reads
 * the lock and the fields are deleted. It landed — `therapist_final_counts`,
 * written once by `finalize_therapist_cycle()` (0076) — so this is that
 * deletion.
 *
 * The count is the field that mattered. TSESSION-069 makes the immutable lock
 * "the SOLE therapist-count input to Rider Session generation", and a number in
 * a request body is a second one — precisely what §2's "Admin or Hub Manager
 * must not manually create extra supply outside the approved generation rules"
 * forbids. `.strict()` means an old caller still sending it now fails loudly
 * instead of having it quietly ignored.
 */
export const generateRiderSessionsSchema = z
  .object({
    hubId: uuidSchema,
    serviceDate: riderSessionDateSchema,
  })
  .strict();

/** What a generation run reports back. Riders never see any of it (§7.1). */
export const riderSessionBatchSchema = z.object({
  id: uuidSchema,
  hubId: uuidSchema,
  serviceDate: riderSessionDateSchema,
  requiredTotal: z.number().int().min(0),
  sessionA: z.number().int().min(0),
  sessionB: z.number().int().min(0),
  sessionC: z.number().int().min(0),
  generatedAt: offsetDateTimeSchema,
  /** True when the generation key already existed — RSG-010, no duplicates. */
  alreadyExisted: z.boolean(),
});

/**
 * There is deliberately NO `SESSION_NOT_ELIGIBLE` code.
 *
 * RIDER-058 forbids telling a Rider that a Session exists but is not theirs to
 * take. `secure_rider_session` (0062) already collapses "already secured" and
 * "never visible to you" into one refusal; giving the API two distinct codes
 * would hand the distinction straight back to any client that switches on them,
 * and re-open at the API layer exactly what the database closed. Both cases are
 * `SESSION_ALREADY_SECURED`.
 */
export const riderSessionErrorCodeSchema = z.enum([
  /** Lost the race, OR was never visible to this Rider — RSG-009, RIDER-058. */
  "SESSION_ALREADY_SECURED",
  /**
   * This Rider already owns a Session covering these hours. Safe to state
   * plainly: the refusal is about their own schedule, which they can already
   * see, so it discloses nothing about supply.
   */
  "SCHEDULE_CONFLICT",
  /** §4.6 — the selected row does not sum to the required total. */
  "GENERATION_CHECKSUM_FAILED",
  /**
   * No Final Therapist Count lock exists for that date and Dispatch Area, so
   * there is no supply figure to generate from (TSESSION-069). It means Friday
   * 7:00 PM finalization has not run for that week — not that the caller got
   * something wrong, and NOT an invitation to supply the number by hand.
   */
  "FINAL_COUNT_NOT_LOCKED",

  // ── lifecycle (0063) ──────────────────────────────────────────────────────
  // These arrive from Postgres in the DETAIL field, not the SQLSTATE — almost
  // every refusal in the lifecycle is a `check_violation`, so the SQLSTATE alone
  // cannot distinguish them and the alternative is string-matching messages.
  // Each value below must exist in exactly one `raise ... detail = '...'`.

  "SESSION_NOT_FOUND",
  /** Someone else's Session. Safe to say — the Rider can already see their own. */
  "SESSION_NOT_YOURS",
  "SESSION_ALREADY_STARTED",
  "SESSION_ALREADY_ENDED",
  /** REND-004: outside the 60-minute window. The server owns this gate. */
  "START_WINDOW_NOT_OPEN",
  /** Inactive account, or wrong Dispatch Area. Documents are NOT checked (OQ-68). */
  "RIDER_NOT_ELIGIBLE",
  "SESSION_IN_PROGRESS",
  /** RIDER-004's 1 km hub proximity. */
  "TOO_FAR_FROM_HUB",
  "SESSION_NOT_ACTIVE",
  "SESSION_NOT_RUNNING",
  "SESSION_NOT_PAUSED",
  "SESSION_NOT_IN_EMERGENCY_PAUSE",
  /** Inside the final 30 minutes (11:30 PM / 12:30 AM / 1:30 AM for A/B/C). */
  "PAUSE_WINDOW_CLOSED",
  /** The 90 minutes are spent. Unused allowance expired; it was never bankable. */
  "PAUSE_ALLOWANCE_EXHAUSTED",
  "PAUSE_LENGTH_INVALID",
  "PAUSE_NOT_FOUND",
  /** This Rider is mid-Dispatch. Also RIDER-037: Dispatch beat Pause to the row. */
  "DISPATCH_IN_PROGRESS",
  /** RIDER-036: an unassigned pending Dispatch anywhere in the area blocks Pause. */
  "AREA_DISPATCH_PENDING",
  "EMERGENCY_CATEGORY_UNKNOWN",
  "REASON_REQUIRED",

  // ── trade (0066) ──────────────────────────────────────────────────────────
  /** Already started — ownership has stopped being a scheduling question. */
  "SESSION_NOT_TRADEABLE",
  /** Past the two-hour mark. A 12:00 PM / B 1:00 PM / C 2:00 PM. */
  "TRADE_DEADLINE_PASSED",
  "TRADE_ALREADY_OPEN",
  /**
   * Taken, expired, or your own offer — ONE code for all three, the same
   * reasoning as SESSION_ALREADY_SECURED. Telling a rider they lost by a second
   * is the disclosure the securing path already avoids.
   */
  "TRADE_NOT_AVAILABLE",
  "TRADE_NOT_OPEN",
]);

/**
 * REND-004 / RIDER-004 — the start action.
 *
 * Coordinates are in the body because the 1 km hub proximity check is
 * server-side and needs them. Nothing else about eligibility is a request field:
 * the rank, the hub, the account state and the 60-minute window are all read
 * server-side from the verified identity and the row, never accepted from the
 * client (`rules/security.md` — the function resolves who you are from the JWT).
 */
export const startRiderSessionSchema = z
  .object({
    sessionId: uuidSchema,
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
  })
  .strict();

/**
 * RIDER-034 — a regular pause. `minutes` is the REQUESTED length; the Rider is
 * charged only what they actually use, and the server clamps the request to
 * both the remaining allowance and the Session end.
 */
export const pauseRiderSessionSchema = z
  .object({
    sessionId: uuidSchema,
    minutes: z.number().int().min(1).max(90),
  })
  .strict();

/** Emergency Pause — started only from Report an Issue. Duration is server-set. */
export const emergencyPauseRiderSessionSchema = z
  .object({
    sessionId: uuidSchema,
    category: riderSessionEmergencyCategorySchema,
  })
  .strict();

export const resumeRiderSessionSchema = z.object({ sessionId: uuidSchema }).strict();

export const endRiderSessionSchema = z.object({ sessionId: uuidSchema }).strict();

/**
 * Terminal exit from an Emergency Pause. The reason is required and recorded —
 * RIDER-028's guarantee review is a human decision made against this record, and
 * this endpoint deliberately does not make it.
 */
export const unableToContinueRiderSessionSchema = z
  .object({
    sessionId: uuidSchema,
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

/**
 * Rider Trade — "Priority Access and Trade" §Trade.
 *
 * Three actions, each carrying only the id it acts on. Nothing about
 * eligibility, the deadline or ownership is a request field: all of it is read
 * server-side from the verified identity and the row, because a client that
 * could send a deadline could send tomorrow's.
 */
export const requestRiderSessionTradeSchema = z.object({ sessionId: uuidSchema }).strict();
export const withdrawRiderSessionTradeSchema = z.object({ tradeId: uuidSchema }).strict();
export const acceptRiderSessionTradeSchema = z.object({ tradeId: uuidSchema }).strict();

export type RiderSessionCode = z.infer<typeof riderSessionCodeSchema>;
export type RequestRiderSessionTradeInput = z.infer<typeof requestRiderSessionTradeSchema>;
export type WithdrawRiderSessionTradeInput = z.infer<typeof withdrawRiderSessionTradeSchema>;
export type AcceptRiderSessionTradeInput = z.infer<typeof acceptRiderSessionTradeSchema>;
export type RiderSessionState = z.infer<typeof riderSessionStateSchema>;
export type RiderSessionOutcome = z.infer<typeof riderSessionOutcomeSchema>;
export type RiderSessionEmergencyCategory = z.infer<typeof riderSessionEmergencyCategorySchema>;
export type StartRiderSessionInput = z.infer<typeof startRiderSessionSchema>;
export type PauseRiderSessionInput = z.infer<typeof pauseRiderSessionSchema>;
export type EmergencyPauseRiderSessionInput = z.infer<typeof emergencyPauseRiderSessionSchema>;
export type ResumeRiderSessionInput = z.infer<typeof resumeRiderSessionSchema>;
export type EndRiderSessionInput = z.infer<typeof endRiderSessionSchema>;
export type UnableToContinueRiderSessionInput = z.infer<typeof unableToContinueRiderSessionSchema>;
export type RiderSessionAccess = z.infer<typeof riderSessionAccessSchema>;
export type RiderRank = z.infer<typeof riderRankSchema>;
export type RiderSessionMaxExtension = z.infer<typeof riderSessionMaxExtensionSchema>;
export type RiderSession = z.infer<typeof riderSessionSchema>;
export type SecureRiderSessionInput = z.infer<typeof secureRiderSessionSchema>;
export type GenerateRiderSessionsInput = z.infer<typeof generateRiderSessionsSchema>;
export type RiderSessionBatch = z.infer<typeof riderSessionBatchSchema>;
export type RiderSessionErrorCode = z.infer<typeof riderSessionErrorCodeSchema>;
